import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Grid2 as Grid,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Button as MuiButton,
} from '@mui/material';
import { isArray } from 'lodash';
import useValidationSchema from './_form';
import { IOrganizationForm, IOrganizationFormComponent } from './_types';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import FormSwitch from 'components/Form/Switch/Switch';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';
import Header from 'components/Header/Header';
import SharedContactSections from '../SharedContactSections';
import { useUser } from 'utils/hooks/useUser';
import { getAllAddressBook } from 'modules/Mdex/AddressBook/_api';
import { IAddressBook } from 'modules/Mdex/AddressBook/_types';

interface IMdexNodePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (entry: IAddressBook) => void;
  addressBooks: IAddressBook[];
}

const MdexNodePickerDialog: React.FC<IMdexNodePickerDialogProps> = ({
  open,
  onClose,
  onConfirm,
  addressBooks,
}) => {
  const { t } = useTranslation('Organizations');
  const [selected, setSelected] = useState<IAddressBook | null>(null);

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mdexDialogTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Autocomplete
            options={addressBooks}
            getOptionLabel={(option) => option.institutionName}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            renderInput={(params) => <TextField {...params} label={t('mdexNode')} />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={handleClose}>{t('cancel')}</MuiButton>
        <MuiButton
          variant="contained"
          disabled={!selected}
          onClick={() => {
            if (selected) {
              onConfirm(selected);
              setSelected(null);
              onClose();
            }
          }}
        >
          {t('confirm')}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

const OrganizationFormComponent: React.FC<IOrganizationFormComponent> = ({
  isCreating,
  defaultValues,
  onSubmit,
  linkBack,
  compactMode = false,
  title,
}) => {
  const { t } = useTranslation('Organizations');
  const { hasRole } = useUser();
  const hasMdexRole = hasRole('ROLE_MDEX_VIEW_ADDRESS_BOOK');

  const [mdexDialogOpen, setMdexDialogOpen] = useState(false);
  const [addressBooks, setAddressBooks] = useState<IAddressBook[]>([]);

  useEffect(() => {
    if (hasMdexRole) {
      const result = getAllAddressBook();
      if (result && typeof result !== 'boolean') {
        result.then((data) => {
          if (isArray(data)) {
            setAddressBooks(data);
          }
        });
      }
    }
  }, [hasMdexRole]);

  const { FormSchema } = useValidationSchema();

  const methods = useForm<IOrganizationForm>({
    resolver: yupResolver(FormSchema),
    defaultValues,
  });

  const { control, handleSubmit, watch, setValue } = methods;

  const mdexNodeUuid = watch('mdexNodeUuid');
  const pairedEntry = addressBooks.find((ab) => ab.uuid === mdexNodeUuid) ?? null;

  const {
    fields: identifierFields,
    append: appendIdentifier,
    remove: removeIdentifier,
  } = useFieldArray({ control, name: 'identifier' });

  const {
    fields: telecomFields,
    append: appendTelecom,
    remove: removeTelecom,
  } = useFieldArray({ control, name: 'telecom' });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: 'address' });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Header title={title} />

        <Papeer>
          <Grid container spacing={compactMode ? 1 : 2}>
            {/* Basic Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">{t('PatientRegistry:basicInfo')}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <FormInput control={control} name="name" label={t('name')} required />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ pt: 1 }}>
                <FormSwitch control={control} name="active" label={t('PatientRegistry:active')} />
              </Box>
            </Grid>

            <SharedContactSections
              control={control}
              compactMode={compactMode}
              identifierFields={identifierFields}
              appendIdentifier={appendIdentifier}
              removeIdentifier={removeIdentifier}
              telecomFields={telecomFields}
              appendTelecom={appendTelecom}
              removeTelecom={removeTelecom}
              addressFields={addressFields}
              appendAddress={appendAddress}
              removeAddress={removeAddress}
            />

            {hasMdexRole && (
              <>
                <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                  <Typography variant="h6">{t('mdexPairing')}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>
                      {pairedEntry ? pairedEntry.institutionName : t('notPaired')}
                    </Typography>
                    <MuiButton variant="outlined" onClick={() => setMdexDialogOpen(true)}>
                      {t('pairMdex')}
                    </MuiButton>
                    {pairedEntry && (
                      <MuiButton
                        variant="outlined"
                        color="error"
                        onClick={() => setValue('mdexNodeUuid', null)}
                      >
                        {t('unpairMdex')}
                      </MuiButton>
                    )}
                  </Box>
                </Grid>
                <MdexNodePickerDialog
                  open={mdexDialogOpen}
                  onClose={() => setMdexDialogOpen(false)}
                  onConfirm={(entry) => setValue('mdexNodeUuid', entry.uuid)}
                  addressBooks={addressBooks}
                />
              </>
            )}
          </Grid>
        </Papeer>

        <Papeer>
          <EntityButtons linkBack={linkBack} />
        </Papeer>
      </form>
    </FormProvider>
  );
};

export default OrganizationFormComponent;
